import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type ButtonIconDirectionConstantType,
    type ButtonIconColorKeyConstantType,
    type ButtonXSmallHeightConstantType,
    type ButtonCustomColorConstantType,
    type ButtonIconDirectionAlias,
    type ButtonColorsConstantType,
    type ButtonDynamicStyleType,
    type ButtonSizeConstantType,
    type ButtonIconDirection,
    type ButtonMeasures,
    type ButtonVariant,
    type ButtonColors
} from "./type";
import {
    type TypeColorVariant,
    TYPE_COLOR_ROLES,
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    type ElevationLevelType,
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getFocusRingStyle,
    getStateColorKey
} from "../../helpers/theme/state";
import {
    getControlMeasures,
    getHitArea
} from "../../helpers/theme/control";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHugStyle
} from "../../helpers/theme/spread";
import {
    getOpaqueColor,
    webStyle
} from "../../utils";

export const BUTTON_ICON_DIRECTIONS: Record<ButtonIconDirection | ButtonIconDirectionAlias, ButtonIconDirection> = {
    start: "start",
    left: "start",
    right: "end",
    end: "end"
};

export const BUTTON_VARIANT_ELEVATIONS: Record<ButtonVariant, ElevationLevelType> = {
    outline: "level1",
    filled: "level1",
    ghost: "level0",
    tonal: "level0",
    link: "level0"
};

export const BUTTON_LINK_TEXT_VARIANTS: Partial<Record<keyof NCoreUIKit.Typography, keyof NCoreUIKit.Typography>> = {
    labelMediumSize: "bodyMediumSize",
    labelSmallSize: "bodySmallSize",
    labelLargeSize: "bodyLargeSize"
};

export const getButtonRoleVariant = (variant: ButtonVariant): TypeColorVariant => {
    if(variant === "link") {
        return "ghost";
    }

    return variant;
};

export const getButtonXSmallHeight = ({
    controlHeights,
    shapeConfigs,
    touchTargets
}: ButtonXSmallHeightConstantType): number => {
    return Math.max(controlHeights.small - shapeConfigs.chipHeightOffset, touchTargets.fine);
};

export const getButtonSize = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    touchTargets,
    iconSizes,
    spaces,
    size
}: ButtonSizeConstantType): ButtonMeasures => {
    if(size === "xSmall") {
        return {
            iconSize: iconSizes.small - shapeConfigs.chipIconOffset,
            paddingHorizontal: spaces.spacingSm,
            minHeight: getButtonXSmallHeight({
                controlHeights,
                shapeConfigs,
                touchTargets
            }),
            valueVariant: "bodySmallSize",
            labelVariant: "labelSmallSize"
        };
    }

    return getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });
};

export const getButtonIconDirection = ({
    iconDirection
}: ButtonIconDirectionConstantType): ButtonIconDirection => {
    return BUTTON_ICON_DIRECTIONS[iconDirection];
};

export const getButtonIconColorKey = ({
    isDisabled,
    variant,
    type
}: ButtonIconColorKeyConstantType): keyof NCoreUIKit.IconContentColors => {
    if(isDisabled) {
        return "disabled";
    }

    const currentRole = TYPE_COLOR_ROLES[type][getButtonRoleVariant(variant)].iconColor;

    if(currentRole.group === "content.icon") {
        return currentRole.key;
    }

    const outlineRole = TYPE_COLOR_ROLES[type].outline.iconColor;

    return outlineRole.group === "content.icon" ? outlineRole.key : "default";
};

export const getButtonCustomColor = ({
    tokenColors = {},
    customColor,
    colors
}: ButtonCustomColorConstantType): string => {
    const _customColor = customColor as keyof NCoreUIKit.ProjectColorPalette;

    return colors.project[_customColor] ?? tokenColors[customColor] ?? customColor;
};

export const getButtonColors = ({
    isForeignTheme = false,
    customBorderColor,
    customIconColor,
    customTextColor,
    customColor,
    isDisabled,
    isOnMedia,
    variant,
    colors,
    type
}: ButtonColorsConstantType): ButtonColors => {
    const currentColors: ButtonColors = {
        ...getTypeColors({
            variant: getButtonRoleVariant(variant),
            isDisabled,
            colors,
            type
        }),
        iconColorKey: getButtonIconColorKey({
            isDisabled,
            variant,
            type
        })
    };

    if(isDisabled && !isOnMedia) {
        return currentColors;
    }

    // A translucent fill would take the page's colour, so a button forced to another theme lays it over its own theme's ground.
    if(isForeignTheme) {
        currentColors.containerColor = getOpaqueColor({
            surface: colors.content.container.default,
            color: currentColors.containerColor
        });
    }

    if(customColor) {
        currentColors.containerColor = getButtonCustomColor({
            tokenColors: colors.content.container,
            customColor,
            colors
        });
    }

    if(customBorderColor) {
        currentColors.borderColor = getButtonCustomColor({
            tokenColors: colors.content.border,
            customColor: customBorderColor,
            colors
        });
    }

    if(isDisabled) {
        currentColors.titleColor = colors.content.text.disabledWhite;

        currentColors.iconColor = colors.content.icon.disabledWhite;

        currentColors.iconColorKey = "disabledWhite";

        return currentColors;
    }

    if(variant === "link" && type === "primary") {
        currentColors.titleColor = colors.content.text.link;
    }

    if(customTextColor) {
        currentColors.titleColor = getButtonCustomColor({
            customColor: customTextColor,
            colors
        });

        currentColors.iconColor = currentColors.titleColor;
    }

    if(customIconColor) {
        currentColors.iconColor = getButtonCustomColor({
            customColor: customIconColor,
            colors
        });
    }

    return currentColors;
};

const stylesheet = StyleSheet.create({
    linkTitle: {
        textDecorationLine: "underline"
    },
    container: {
        justifyContent: "center",
        alignItems: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    surface: {
        justifyContent: "center",
        borderStyle: "solid",
        alignItems: "center"
    },
    content: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1
    },
    loadingSlot: {
        justifyContent: "center",
        overflow: "hidden"
    },
    loadingContainer: {
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        bottom: 0,
        start: 0,
        top: 0,
        end: 0
    },
    title: {
        alignSelf: "baseline",
        textAlign: "center",
        flexShrink: 1
    },
    fillSurface: {
        alignSelf: "stretch",
        flexGrow: 1
    }
});

export const useStyles = ({
    verticalLocationForStretchFix,
    isFixVerticalStretch,
    isCustomPadding,
    spreadBehaviour,
    isFocusVisible,
    currentColors,
    borderRadius,
    shapeConfigs,
    touchTargets,
    currentSize,
    pointerType,
    shadowAlpha,
    elevations,
    isAttached,
    isDisabled,
    isIconOnly,
    isLoading,
    userStyle,
    borders,
    variant,
    colors,
    spaces,
    type
}: ButtonDynamicStyleType) => {
    const hitArea = isAttached ? {
        minHeight: currentSize.minHeight,
        minWidth: currentSize.minHeight
    } : getHitArea({
        visualSize: currentSize.minHeight,
        touchTargets,
        pointerType
    });

    const currentElevation = BUTTON_VARIANT_ELEVATIONS[variant];

    const styles = {
        container: {
            alignSelf: isFixVerticalStretch ? verticalLocationForStretchFix === "center" ? verticalLocationForStretchFix : `flex-${verticalLocationForStretchFix}` : undefined,
            minHeight: hitArea.minHeight,
            minWidth: hitArea.minWidth
        } as Mutable<ViewStyle>,
        surface: {
            paddingHorizontal: currentSize.paddingHorizontal,
            backgroundColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            minHeight: currentSize.minHeight,
            minWidth: currentSize.minHeight,
            ...getCornerStyle({
                radius: borderRadius,
                shapeConfigs
            }),
            borderWidth: borders.line
        } as Mutable<ViewStyle>,
        content: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    if(!isDisabled && currentColors.containerColor !== "transparent" && elevations[currentElevation].length) {
        Object.assign(styles.surface, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: currentElevation,
            shadowAlpha,
            elevations
        }));
    }

    Object.assign(styles.surface, getFocusRingStyle({
        colorKey: getStateColorKey({
            isDisabled,
            type
        }),
        isFocusVisible,
        borders,
        colors
    }));

    if(isIconOnly || isCustomPadding) {
        delete styles.surface.paddingHorizontal;
    }

    if(variant === "link") {
        delete styles.surface.paddingHorizontal;

        delete styles.surface.minHeight;

        delete styles.surface.minWidth;

        styles.surface.borderWidth = 0;
    }

    if(isLoading) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    if(spreadBehaviour === "baseline") {
        Object.assign(styles.container, getHugStyle({
            style: userStyle
        }));
    }

    if(spreadBehaviour === "stretch") {
        styles.container.alignSelf = spreadBehaviour;
        styles.container.flexShrink = 1;
        styles.container.width = "100%";
        styles.surface.alignSelf = spreadBehaviour;
    }

    return styles;
};
export default stylesheet;
